import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import createAxiosInstance from '../../support/AxiosSupport';
import {UserIcon, CloseIcon} from '../../components/icons';
import UserSearchSelect from '../../components/UserSearchSelect';
import {ALL_PERMISSIONS} from '../../context/WardrobeContext';
import IconButton from "../../components/IconButton";

interface AccessGrant {
    id: string;
    grantedToUserId: string;
    grantedToUserDisplay?: string;
    permissions: string[];
    createdAt: string;
}

interface PermOption {
    value: string;
    label: string;
}

const PERM_GROUPS: {title: string; keys: string[]}[] = [
    {title: 'Просмотр',  keys: ['VIEW_ITEMS', 'VIEW_THROWN_AWAY']},
    {title: 'Вещи',      keys: ['EDIT_ITEMS', 'ADD_ITEMS']},
    {title: 'Выбытие',   keys: ['MOVE_TO_TRASH', 'THROW_AWAY_IMMEDIATE', 'CLEAR_TRASH']},
    {title: 'Списки',    keys: ['VIEW_LISTS', 'MANAGE_LIST_ITEMS', 'MANAGE_LISTS']},
    {title: 'Комментарии', keys: ['ADD_COMMENTS']},
];

const PermCheckboxes: React.FC<{
    options: PermOption[];
    selected: Set<string>;
    onChange: (v: string) => void;
}> = ({options, selected, onChange}) => (
    <div className="grid gap-4 sm:grid-cols-2">
        {PERM_GROUPS.map(group => {
            const opts = group.keys.map(k => options.find(o => o.value === k)).filter(Boolean) as PermOption[];
            if (!opts.length) return null;
            return (
                <div key={group.title} className="rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">{group.title}</p>
                    <div className="space-y-2">
                        {opts.map(opt => (
                            <label key={opt.value} className="flex items-center gap-2 cursor-pointer py-0.5 hover:text-primary-600 transition-colors">
                                <input type="checkbox" checked={selected.has(opt.value)} onChange={() => onChange(opt.value)}
                                       className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"/>
                                <span className="text-sm">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            );
        })}
    </div>
);

const ProfileAccessPage: React.FC = () => {
    const [grants, setGrants] = useState<AccessGrant[]>([]);
    const [loading, setLoading] = useState(false);
    const [permOptions, setPermOptions] = useState<PermOption[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [grantUserId, setGrantUserId] = useState('');
    const [grantPerms, setGrantPerms] = useState<Set<string>>(new Set(['VIEW_ITEMS']));
    const [grantSaving, setGrantSaving] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingPerms, setEditingPerms] = useState<Set<string>>(new Set());

    const fetchGrants = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<AccessGrant[]>('/wardrobe-access/grants');
            setGrants(res.data || []);
        } catch {
            setGrants([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPermOptions = useCallback(async () => {
        try {
            const api = await createAxiosInstance();
            const res = await api.get<PermOption[]>('/wardrobe-access/permissions');
            setPermOptions(res.data || []);
        } catch {}
    }, []);

    useEffect(() => {
        fetchGrants();
        fetchPermOptions();
    }, [fetchGrants, fetchPermOptions]);

    const togglePerm = (set: Set<string>, v: string): Set<string> => {
        const next = new Set(set);
        next.has(v) ? next.delete(v) : next.add(v);
        return next;
    };

    const handleGrant = async () => {
        if (!grantUserId.trim() || grantPerms.size === 0) return;
        setGrantSaving(true);
        try {
            const api = await createAxiosInstance();
            await api.post('/wardrobe-access', {grantedToUserId: grantUserId.trim(), permissions: Array.from(grantPerms)});
            setGrantUserId('');
            setGrantPerms(new Set(['VIEW_ITEMS']));
            setDrawerOpen(false);
            fetchGrants();
        } finally {
            setGrantSaving(false);
        }
    };

    const handleUpdate = async (id: string) => {
        if (editingPerms.size === 0) return;
        try {
            const api = await createAxiosInstance();
            await api.put(`/wardrobe-access/${id}`, {permissions: Array.from(editingPerms)});
            setEditingId(null);
            fetchGrants();
        } catch {}
    };

    const handleRevoke = async (id: string) => {
        try {
            const api = await createAxiosInstance();
            await api.delete(`/wardrobe-access/${id}`);
            fetchGrants();
        } catch {}
    };

    const permLabel = (g: AccessGrant) =>
        (g.permissions || []).length >= ALL_PERMISSIONS.length
            ? 'Полный доступ'
            : (g.permissions || []).map(p => permOptions.find(o => o.value === p)?.label ?? p).join(' · ');

    return (
        <div className="space-y-6">
            {/* Список */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                <div className="px-6 py-5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Кому предоставлен доступ
                    </h3>
                    <button onClick={() => setDrawerOpen(true)} className="btn-primary py-1.5 px-4 text-sm">
                        + Пригласить
                    </button>
                </div>
                <div className="p-6">
                    {loading ? (
                        <div className="py-10 text-center text-gray-500 dark:text-gray-400">Загрузка...</div>
                    ) : grants.length === 0 ? (
                        <div className="py-10 text-center">
                            <p className="text-gray-500 dark:text-gray-400">Доступ никому не предоставлен</p>
                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                Нажмите «+ Пригласить», чтобы выдать доступ
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {grants.map(g => (
                                <div key={g.id} className="rounded-lg border border-gray-200 dark:border-gray-600 p-4 hover:border-gray-300 dark:hover:border-gray-500 transition-colors">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
                                                <UserIcon className="w-5 h-5 text-primary-600 dark:text-primary-400"/>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                                    {g.grantedToUserDisplay || g.grantedToUserId}
                                                </p>
                                                {editingId !== g.id && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                        {permLabel(g)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            {editingId === g.id ? (
                                                <>
                                                    <button onClick={() => handleUpdate(g.id)} className="btn-primary py-1.5 px-3 text-sm">Сохранить</button>
                                                    <button onClick={() => setEditingId(null)} className="btn-secondary py-1.5 px-3 text-sm">Отмена</button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => { setEditingId(g.id); setEditingPerms(new Set(g.permissions || [])); }}
                                                            className="btn-secondary py-1.5 px-3 text-sm">
                                                        Изменить
                                                    </button>
                                                    <button onClick={() => handleRevoke(g.id)}
                                                            className="py-1.5 px-3 text-sm rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                                        Отозвать
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {editingId === g.id && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Права</p>
                                            <PermCheckboxes options={permOptions} selected={editingPerms}
                                                            onChange={v => setEditingPerms(prev => togglePerm(prev, v))}/>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Drawer-оверлей «Пригласить» */}
            {drawerOpen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)}/>
                    <div className="relative w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl flex flex-col h-full overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Выдать доступ</h3>
                            <IconButton
                                label="Закрыть"
                                onClick={() => setDrawerOpen(false)}
                                className="rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <CloseIcon className="w-5 h-5"/>
                            </IconButton>
                        </div>
                        <div className="flex-1 p-6 space-y-6">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Найдите пользователя и выберите права. В поиске — пользователи, которые хотя бы раз входили в приложение.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Пользователь</label>
                                <UserSearchSelect value={grantUserId} onChange={setGrantUserId}
                                                  placeholder="Поиск по имени или логину" minSearchLength={2}/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Права доступа</label>
                                <PermCheckboxes options={permOptions} selected={grantPerms}
                                                onChange={v => setGrantPerms(prev => togglePerm(prev, v))}/>
                            </div>
                        </div>
                        <div className="px-6 py-5 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                            <button onClick={handleGrant} disabled={!grantUserId.trim() || grantPerms.size === 0 || grantSaving}
                                    className="btn-primary flex-1">
                                {grantSaving ? 'Добавление...' : 'Добавить'}
                            </button>
                            <button onClick={() => setDrawerOpen(false)} className="btn-secondary">Отмена</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default observer(ProfileAccessPage);
